import React, { useEffect, useRef, useState } from 'react';
import { AVISO_SIN_CLIENTE, BuroPortal } from './helpers';
import { AvisoCodigoPostal, BuroCampo } from './BuroCampo';
import { useCodigoPostal } from './useCodigoPostal';
import { COL, FICHA, erroresDe, nacimientoDeRfc, REGLAS_RFC, rfcVsNacimiento, valorDe, verFecha } from './columnas';

const SECCIONES = [['persona', 'Persona'], ['domicilio', 'Domicilio'], ['empleo', 'Empleo']];
// Campos que ocupan el ancho completo de la ficha.
const ANCHOS = new Set(['17', '18', '26']);

/*
 * P4b · Ficha del cliente: captura junta de persona, domicilio y empleo. Se
 * edita sobre la primera fila del cliente y todo se guarda "también en el
 * sistema", así que el backend lo aplica a todos sus contratos.
 *
 * props:
 *   filas        contratos del cliente en el reporte (la primera da los valores)
 *   todas        todas las filas del reporte (sugerencias de colonia/ciudad/empresa)
 *   cola         { pos, total, siguiente } cuando se abrió desde "Clientes con datos faltantes"
 *   onGuardar    recibe [{columna, valor}] solo con lo que cambió
 */
export const BuroFichaCliente = ({ filas, todas, cola, guardando, error, onClose, onGuardar }) => {
  const fila = filas[0];
  // Si algún contrato del cliente no tiene cliente ligado, todo va solo al reporte.
  const soloReporte = filas.some((f) => !f.cliente_ligado);
  const [d, setD] = useState(() => {
    const ini = {};
    [...FICHA.persona, ...FICHA.domicilio, ...FICHA.empleo, 'tipo_persona'].forEach((k) => { ini[k] = valorDe(fila, COL[k]); });
    return ini;
  });
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));

  // C.P. completo y encontrado → el C.P. manda: estado, municipio y ciudad se
  // sobrescriben (siguen editables). Colonia: si hay una sola se elige sola; si
  // hay varias queda el select sin elegir por el usuario.
  const cp = useCodigoPostal(d['23']);
  const llenado = useRef(null);
  useEffect(() => {
    if (cp.estado !== 'ok' || llenado.current === d['23']) return;
    llenado.current = d['23'];
    const x = cp.datos;
    setD((v) => ({
      ...v,
      '22': x.estado || v['22'],
      '20': x.municipio || v['20'],
      '21': x.ciudad || v['21'],
      '19': v['19'] || (x.colonias?.length === 1 ? x.colonias[0] : ''),
    }));
  }, [cp, d]);

  const cambios = Object.keys(d)
    .filter((k) => d[k] !== valorDe(fila, COL[k]))
    .map((k) => ({ columna: k === 'tipo_persona' ? 0 : Number(k), valor: d[k] }));

  const campo = (k) => {
    const c = COL[k];
    // RFC y nacimiento se revisan en vivo con el formulario; los errores
    // rfc_formato / rfc_fecha del servidor (último guardado) solo valen mientras
    // no se pueda comparar. Los demás errores del servidor siguen hasta guardar.
    const vivo = rfcVsNacimiento(d['10'], d['9']);
    const delServidor = erroresDe(fila, c);
    let errs = delServidor;
    if (k === '10') {
      if (vivo.estado !== 'indeterminado') errs = delServidor.filter((e) => !REGLAS_RFC.includes(e.regla));
      if (vivo.mensaje) errs = [...errs, { mensaje: vivo.mensaje }];
    }
    const err = errs[0];
    // Fecha distinta: también se marca el campo de nacimiento.
    let rfcFecha = null;
    if (k === '9') {
      rfcFecha = vivo.estado === 'indeterminado'
        ? fila.errores.find((e) => e.regla === 'rfc_fecha')
        : (vivo.estado === 'no_coincide' ? { mensaje: vivo.mensaje } : null);
    }
    const sug = !err && fila.fuentes[k] === 'sugerencia';
    const cls = err || rfcFecha ? 'err' : sug ? 'sug' : '';
    return (
      <div key={k} className={ANCHOS.has(k) ? 'full' : ''}>
        <div className="bu-ficha-lbl">
          <span>{c.label}</span><span className="n">· {c.num}</span>
          {(err || rfcFecha) && <i className="pi pi-exclamation-triangle" title={(err || rfcFecha).mensaje} style={{ fontSize: 11, color: '#B91C1C' }} />}
          {sug && <i className="pi pi-info-circle" title={fila.notas[k] || 'Sugerencia: confírmala'} style={{ fontSize: 11, color: '#8A6D2A' }} />}
        </div>
        <BuroCampo
          col={c}
          value={d[k]}
          onChange={(v) => set(k, v)}
          filas={todas}
          estado={d['22']}
          colonias={k === '19' && cp.estado === 'ok' ? cp.datos.colonias : null}
          className={`bu-input ${cls}`}
        />
        {k === '23' && (
          <AvisoCodigoPostal cp={cp} />
        )}
        {k === '9' && nacimientoDeRfc(d['10']) && nacimientoDeRfc(d['10']) !== d[k] && (
          <div className="bu-sub" style={{ fontSize: 11, marginTop: 4 }}>
            Sugerida desde el RFC: {verFecha(nacimientoDeRfc(d['10']))}{' '}
            <button type="button" className="bu-btn bu-btn-link" style={{ height: 20, padding: '0 6px' }} onClick={() => set(k, nacimientoDeRfc(d['10']))}>Usar</button>
          </div>
        )}
        {k === '9' && !d['10'] && !d[k] && <div className="bu-sub" style={{ fontSize: 11, marginTop: 4 }}>Sin RFC: captúrala del INE.</div>}
        {err && <div style={{ fontSize: 11, color: '#B91C1C', marginTop: 4, lineHeight: 1.4 }}>{err.mensaje}</div>}
        {k === '10' && vivo.estado === 'ok' && (
          <div style={{ fontSize: 11, color: '#1B7F4D', marginTop: 4 }}><i className="pi pi-check" style={{ fontSize: 10 }} /> RFC y nacimiento coinciden</div>
        )}
        {rfcFecha && !err && <div style={{ fontSize: 11, color: '#B91C1C', marginTop: 4 }}>No coincide con la fecha del RFC (ver el detalle en RFC).</div>}
        {fila.notas[k] && !err && <div className="bu-sub" style={{ fontSize: 11, marginTop: 4 }}>{fila.notas[k]}</div>}
      </div>
    );
  };

  return (
    <BuroPortal>
      <div className="bu-panel" style={{ width: 560 }}>
        <div className="bu-panel-head">
          <div style={{ flex: 1 }}>
            <h3>Datos del cliente para Buró</h3>
            <div className="s">
              {fila.cliente} · {filas.length === 1 ? '1 contrato' : `${filas.length} contratos`} ({filas.map((f) => f.numero_cuenta).join(', ')})
            </div>
          </div>
          {cola && (
            <span className="bu-chip" style={{ alignSelf: 'center', background: '#FDECEC', color: '#B91C1C' }}>Cliente {cola.pos} de {cola.total}</span>
          )}
          <button type="button" className="bu-icon-btn" onClick={onClose} aria-label="Cerrar"><i className="pi pi-times" /></button>
        </div>

        <div className="bu-panel-body" style={{ gap: 24 }}>
          {soloReporte && (
            <div className="bu-note bu-note-warn"><i className="pi pi-info-circle" /><span>{AVISO_SIN_CLIENTE}.</span></div>
          )}
          {error && <div className="bu-note bu-note-err"><i className="pi pi-exclamation-triangle" /><span style={{ whiteSpace: 'pre-line' }}>{error}</span></div>}
          {SECCIONES.map(([sec, titulo]) => (
            <div key={sec} className="bu-ficha-sec">
              <h4>{titulo}</h4>
              <div className="bu-ficha-grid">
                {FICHA[sec].map(campo)}
              </div>
            </div>
          ))}

          <div className="bu-ficha-sec" style={{ gap: 10 }}>
            <h4>Tipo de persona</h4>
            <div style={{ display: 'flex', gap: 20 }}>
              {['PF', 'PFAE', 'PM'].map((t) => (
                <label key={t} className="bu-radio" style={{ gap: 8 }}>
                  <input type="radio" name="tipo" checked={d.tipo_persona === t} onChange={() => set('tipo_persona', t)} />{t}
                </label>
              ))}
            </div>
            {soloReporte && <div className="bu-sub" style={{ fontSize: 12 }}>El tipo se guarda en el contrato.</div>}
            {d.tipo_persona && d.tipo_persona !== 'PF' && (
              <div style={{ fontSize: 12, color: '#0E5E80' }}>Va en el reporte de Buró Empresas. Sus contratos saldrán de este reporte.</div>
            )}
          </div>

          {!soloReporte && <div className="bu-note bu-note-ok">
            <i className="pi pi-check-circle" />
            <span>
              Se guarda en el cliente y se aplica a {filas.length === 1 ? 'su contrato' : `sus ${filas.length} contratos`} en este y los siguientes reportes.
            </span>
          </div>}
        </div>

        <div className="bu-panel-foot" style={{ justifyContent: 'flex-end' }}>
          {cola?.siguiente && <span className="bu-sub" style={{ fontSize: 12, marginRight: 'auto' }}>Siguiente: {cola.siguiente}</span>}
          <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
          <button type="button" className="bu-btn bu-btn-primary" disabled={guardando} onClick={() => onGuardar(cambios, soloReporte)}>
            {guardando ? 'Guardando…' : cola ? 'Guardar y siguiente' : 'Guardar'}
          </button>
        </div>
      </div>
    </BuroPortal>
  );
};

export default BuroFichaCliente;
